import { useCallback, useEffect, useRef, useState } from "react";
import { FileText, Upload, Download, Trash2, FolderOpen } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";

const fmtSize = (n) => (n < 1024 ? `${n} B` : n < 1048576 ? `${Math.round(n / 1024)} KB` : `${(n / 1048576).toFixed(1)} MB`);

export default function Documents() {
  const [docs, setDocs] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [confirm, setConfirm] = useState(null);
  const fileRef = useRef(null);

  const load = useCallback(() => {
    api.get("/documents").then((r) => setDocs(r.data)).catch(() => toast.error("Failed to load documents"));
  }, []);
  useEffect(() => { load(); }, [load]);

  const upload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    try {
      for (const file of files) {
        const fd = new FormData();
        fd.append("file", file);
        await api.post("/documents", fd);
      }
      toast.success(files.length > 1 ? `${files.length} files uploaded` : "File uploaded");
      load();
    } catch {
      toast.error("Upload failed");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const download = async (att) => {
    try {
      const res = await api.get(`/attachments/${att.id}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = att.original_filename;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Download failed");
    }
  };

  const remove = async (attId) => {
    try {
      await api.delete(`/attachments/${attId}`);
      toast.success("Document removed");
      load();
    } catch {
      toast.error("Failed to remove document");
    }
  };

  return (
    <div className="space-y-5" data-testid="documents-page">
      <div className="flex flex-wrap items-center gap-3">
        <div className="mr-auto">
          <h1 className="font-heading text-2xl font-semibold tracking-tight text-slate-900 sm:text-3xl">Documents</h1>
          <p className="mt-1 text-sm text-slate-500">Shared company document library — policies, forms, templates, media.</p>
        </div>
        <Button className="rounded-full bg-[#2C5545] text-white hover:bg-[#1E3B30]" onClick={() => fileRef.current?.click()} disabled={uploading} data-testid="upload-doc-btn">
          <Upload className="mr-2 h-4 w-4" /> {uploading ? "Uploading…" : "Upload Files"}
        </Button>
        <input ref={fileRef} type="file" multiple className="hidden" onChange={upload} data-testid="doc-file-input" />
      </div>

      {!docs ? (
        <p className="py-16 text-center text-sm text-slate-400">Loading…</p>
      ) : docs.length === 0 ? (
        <Card className="rounded-2xl border-dashed border-slate-300" data-testid="documents-empty">
          <CardContent className="flex flex-col items-center py-16 text-center">
            <FolderOpen className="mb-3 h-10 w-10 text-slate-300" strokeWidth={1.5} />
            <p className="text-sm text-slate-500">No documents yet. Upload company files to share them here.</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-3">
          {docs.map((att) => (
            <Card key={att.id} className="rounded-2xl border-slate-200 transition-all duration-150 hover:-translate-y-0.5 hover:shadow-md" data-testid={`doc-${att.id}`}>
              <CardContent className="flex items-center gap-3 p-4">
                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-[#2C5545]/10">
                  <FileText className="h-5 w-5 text-[#2C5545]" strokeWidth={1.5} />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-semibold text-slate-900">{att.original_filename}</p>
                  <p className="text-xs text-slate-400">{fmtSize(att.size)} · {att.created_at?.slice(0, 10)}</p>
                </div>
                <button className="rounded-full p-2 text-slate-400 transition-colors hover:bg-[#2C5545]/10 hover:text-[#2C5545]" onClick={() => download(att)} data-testid={`download-doc-${att.id}`}><Download className="h-4 w-4" /></button>
                <button className="rounded-full p-2 text-slate-300 transition-colors hover:bg-rose-50 hover:text-rose-600" onClick={() => setConfirm(att)} data-testid={`delete-doc-${att.id}`}><Trash2 className="h-4 w-4" /></button>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <AlertDialog open={!!confirm} onOpenChange={(o) => !o && setConfirm(null)}>
        <AlertDialogContent className="rounded-lg" data-testid="doc-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Remove {confirm?.original_filename}?</AlertDialogTitle>
            <AlertDialogDescription>This action cannot be undone.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="doc-confirm-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction className="bg-rose-600 hover:bg-rose-700" onClick={() => { remove(confirm.id); setConfirm(null); }} data-testid="doc-confirm-action">Remove</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
